import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { getErrorMessage } from '@/lib/api'
import { locationsApi } from '@/services/locations'
import { workersApi, type WorkerLeaveRequest } from '@/services/workers'
import { WorkerProfileLink } from '@/pages/attendance/worker-link'
import {
  addIsoDays,
  formatClock,
  formatDay,
  methodLabel,
  punchDate,
  punchDeviceLabel,
  punchWorkerId,
  todayIsoDate,
  useAttendanceDirectory,
  useAttendanceOrgTimeZone,
  useAttendancePunches,
  workerCode,
  workerDepartment,
  workerName,
  workerTitle,
  type AttendancePunch,
} from '@/pages/attendance/data'

function openCheckInDays(day: string, today: string) {
  const [startYear, startMonth, startDay] = day.split('-').map(Number)
  const [endYear, endMonth, endDay] = today.split('-').map(Number)
  const start = Date.UTC(startYear, (startMonth || 1) - 1, startDay || 1)
  const end = Date.UTC(endYear, (endMonth || 1) - 1, endDay || 1)
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 1
  return Math.round((end - start) / 86400000)
}

export default function LiveAttendancePage({ embedded = false }: { embedded?: boolean }) {
  const today = todayIsoDate(useAttendanceOrgTimeZone())
  const { workers, isLoading: workersLoading, error: workersError } = useAttendanceDirectory()
  const punchesQuery = useAttendancePunches(addIsoDays(today, -30), addIsoDays(today, 1))
  const punches = (punchesQuery.data?.results ?? []) as AttendancePunch[]
  const leavesQuery = useQuery({
    queryKey: ['attendance-live-leaves', today],
    queryFn: () => workersApi.getAdminLeaveRequests({ status: 'approved', on_date: today }),
  })
  const leaves = leavesQuery.data ?? []
  const [locationFilter, setLocationFilter] = useState('all')
  const locationsQuery = useQuery({
    queryKey: ['attendance-live-locations'],
    queryFn: () => locationsApi.list(),
  })

  const rows = useMemo(() => {
    const punchByWorker = new Map<string, AttendancePunch>()
    const previousOpenByWorker = new Map<string, AttendancePunch>()
    punches.forEach((punch) => {
      const workerId = punchWorkerId(punch)
      if (!workerId) return
      const day = punchDate(punch)
      if (!day) return
      if (day < today) {
        if (punch.check_out) return
        const currentOpen = previousOpenByWorker.get(workerId)
        if (!currentOpen || String(punch.check_in || '') > String(currentOpen.check_in || '')) {
          previousOpenByWorker.set(workerId, punch)
        }
        return
      }
      if (day !== today) return
      const open = !punch.check_out
      const current = punchByWorker.get(workerId)
      if (!current) {
        punchByWorker.set(workerId, punch)
        return
      }
      const currentOpen = !current.check_out
      if (open !== currentOpen) {
        if (open) punchByWorker.set(workerId, punch)
        return
      }
      if (String(punch.check_in || '') > String(current.check_in || '')) punchByWorker.set(workerId, punch)
    })
    const leaveByWorker = new Map<string, WorkerLeaveRequest>()
    leaves.forEach((leave) => {
      if (leave.start_date > today || leave.end_date < today) return
      leaveByWorker.set(String(leave.worker), leave)
    })
    return workers.map((worker) => {
      const ids = [String(worker.worker_id ?? ''), String(worker.id)].filter(Boolean)
      const punch = ids.map((id) => punchByWorker.get(id)).find(Boolean)
      const previousOpen = ids.map((id) => previousOpenByWorker.get(id)).find(Boolean)
      const leave = ids.map((id) => leaveByWorker.get(id)).find(Boolean)
      const clockedIn = Boolean(punch && !punch.check_out)
      return { worker, punch, previousOpen, leave, clockedIn }
    }).sort((left, right) => {
      const rank = (row: { clockedIn: boolean; leave?: WorkerLeaveRequest; punch?: AttendancePunch }) => (
        row.clockedIn ? 0 : row.leave ? 1 : row.punch ? 2 : 3
      )
      const byStatus = rank(left) - rank(right)
      if (byStatus !== 0) return byStatus
      return workerName(left.worker).localeCompare(workerName(right.worker))
    })
  }, [leaves, punches, today, workers])

  const locationOptions = useMemo(() => {
    const options = new Map<string, string>()
    for (const location of locationsQuery.data ?? []) {
      if (location.deleted || location.active === false) continue
      options.set(String(location.id), location.place || `Location ${location.id}`)
    }
    for (const row of rows) {
      const id = row.punch?.attendance_location_id
      const name = row.punch?.attendance_location_name
      if (id != null && name && !options.has(String(id))) options.set(String(id), name)
    }
    return [...options.entries()].sort((left, right) => left[1].localeCompare(right[1]))
  }, [locationsQuery.data, rows])

  const visibleRows = locationFilter === 'all'
    ? rows
    : rows.filter((row) => String(row.punch?.attendance_location_id ?? '') === locationFilter)
  const clockedInCount = visibleRows.filter((row) => row.clockedIn).length
  const leaveCount = visibleRows.filter((row) => row.leave).length

  return (
    <div className="space-y-5 pb-6">
      {embedded ? null : (
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Live</h1>
            <p className="mt-1 text-sm text-slate-500">Who has clocked in today, and who is on leave today.</p>
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        <CountCard label="Workers" value={visibleRows.length} tone="slate" loading={workersLoading || punchesQuery.isLoading || leavesQuery.isLoading} />
        <CountCard label="Clocked in" value={clockedInCount} tone="emerald" loading={workersLoading || punchesQuery.isLoading || leavesQuery.isLoading} />
        <CountCard label="On leave" value={leaveCount} tone="amber" loading={workersLoading || punchesQuery.isLoading || leavesQuery.isLoading} />
      </div>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-end gap-3 border-b border-slate-100 px-4 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={locationFilter} onValueChange={setLocationFilter}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="All locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All locations</SelectItem>
              {locationOptions.map(([id, name]) => (
                <SelectItem key={id} value={id}>{name}</SelectItem>
              ))}
            </SelectContent>
            </Select>
          </div>
        </div>
        {workersLoading || punchesQuery.isLoading || leavesQuery.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-12 w-full" />)}
          </div>
        ) : workersError || punchesQuery.error || leavesQuery.error ? (
          <p className="p-6 text-sm text-destructive">{getErrorMessage(workersError || punchesQuery.error || leavesQuery.error)}</p>
        ) : visibleRows.length === 0 ? (
          <p className="p-8 text-center text-sm text-slate-500">
            {locationFilter === 'all' ? 'No workers to show.' : 'No workers at this location.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Employee</th>
                  <th className="px-4 py-3 font-medium">Employee ID</th>
                  <th className="px-4 py-3 font-medium">Department</th>
                  <th className="px-4 py-3 font-medium">Location</th>
                  <th className="px-4 py-3 font-medium">Check In</th>
                  <th className="px-4 py-3 font-medium">Clock Method</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map(({ worker, punch, previousOpen, leave, clockedIn }) => {
                  const title = workerTitle(worker)
                  const leaveLabel = leave?.leave_type_name || 'Leave'
                  const previousCheckIn = previousOpen?.check_in_local || previousOpen?.check_in
                  const openDays = previousOpen ? openCheckInDays(punchDate(previousOpen), today) : 0
                  return (
                    <tr key={worker.id} className="border-t border-slate-100">
                      <td className="px-4 py-3">
                        <WorkerProfileLink workerId={worker.worker_id ?? worker.id} name={workerName(worker)} className="font-medium text-slate-900" />
                        {title ? <div className="text-xs text-slate-500">{title}</div> : null}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{workerCode(worker) || '—'}</td>
                      <td className="px-4 py-3 text-slate-600">{workerDepartment(worker)}</td>
                      <td className="px-4 py-3 text-slate-600">{punch?.attendance_location_name || '—'}</td>
                      <td className="px-4 py-3 tabular-nums text-slate-700">
                        {punch ? (
                          formatClock(punch.check_in_local || punch.check_in)
                        ) : previousCheckIn ? (
                          <div>
                            <div>{formatClock(previousCheckIn)}</div>
                            <div className="text-xs font-normal text-slate-500">{formatDay(previousCheckIn)}</div>
                          </div>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        <div>{punch ? methodLabel(punch.source) : '—'}</div>
                        {punchDeviceLabel(punch) ? (
                          <div className="text-[10px] leading-tight text-slate-500">{punchDeviceLabel(punch)}</div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {clockedIn ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">Clocked In</span> : null}
                          {leave ? <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">On Leave · {leaveLabel}</span> : null}
                          {!clockedIn && !leave && punch ? <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">Clocked Out</span> : null}
                          {!clockedIn && !leave && !punch && !previousOpen ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">Not Clocked In</span> : null}
                          {previousOpen ? (
                            <span
                              className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800"
                              title={previousCheckIn ? `Checked in ${formatDay(previousCheckIn)} ${formatClock(previousCheckIn)}` : undefined}
                            >
                              {`Checked-in +${openDays} ${openDays === 1 ? 'day' : 'days'}`}
                            </span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

function CountCard({ label, value, tone, loading }: { label: string; value: number; tone: 'slate' | 'emerald' | 'amber'; loading: boolean }) {
  const toneClass = {
    slate: 'border-slate-200 bg-slate-50 text-slate-900',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
  }[tone]
  return (
    <div className={`rounded-2xl border px-5 py-4 shadow-sm ${toneClass}`}>
      <div className="text-sm font-medium">{label}</div>
      {loading ? <Skeleton className="mt-2 h-10 w-16" /> : <div className="mt-1 text-4xl font-semibold tabular-nums leading-none">{value}</div>}
    </div>
  )
}
